<template>
  <div class="slider-wrapper" :class="[{ isLoading: isLoading }]" >
    <placeholder v-if="hasError" />
    <div v-else :class="[generateSliderClass]">
      <div v-for="slide in sliderContent.slides" :key="slide.index">
        <slide :content="slide"></slide>
      </div>
    </div>
  </div>
</template> 

<script>
  import { tns } from "tiny-slider/src/tiny-slider.module";
  import Content from "./slider-content";
  import Slide from "./Slide";
  import Placeholder from "../placeholder/Placeholder";
  import { Props, Computed } from '../../assets/js/mixins';

  let sliderContent;
  let slider = null;

  export default {
    name: "Slider",
    mixins: [Props, Computed],
    data() {
      return {
        hasError: false,
        isLoading: true,
        autoHeight: false,
        sliderContent: {},
        sliderOptions: {
          autoplayHoverPause: true,
          autoplayButtonOutput: false,
          mouseDrag: true,
          controlsText: [
            '<svg fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/><path d="M0 0h24v24H0z" fill="none"/></svg>',
            '<svg fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/><path d="M0 0h24v24H0z" fill="none"/></svg>'
          ]
        }
      };
    },
    computed: {
      hasContentUrl() {
        if (typeof this.content === 'string') {
          return true;
        } else {
          return false;
        }
      },
      generateSliderClass() {
        this.sliderOptions.container = `.tns-slides-${this.sliderHash}`
        return `tns-slides-${this.sliderHash}`
      }
    },
    created() {
      if (this.hasContentUrl) {
        sliderContent = new Content(this.content);
      }
    },
    beforeMount() {
      if (this.hasContentUrl) {
        let vm = this;
        sliderContent.formatSliderContent().then(() => {
          vm.sliderContent = sliderContent.content
          vm.sliderOptions = {...vm.sliderOptions, ...sliderContent.content.options}
          vm.isLoading = false;
        }, () => {
          vm.hasError = true;
        });
      } else {
        this.isLoading = false;
        this.sliderOptions = {...this.sliderOptions, ...this.content.options}
        this.sliderContent = this.content
      }
    },
    updated() {
      if (slider === null) {
        slider = tns(this.sliderOptions);
      }
    },
    components: {
      Slide,
      Placeholder
    }
  };
</script>

<style lang="scss">
  @import "../../assets/scss/mixins.scss";
  @import "../../assets/scss/variables.scss";

  .slider-wrapper {
    min-height: 100%;
    @include stretch;
    position: relative;
  }

  .tns-controls {
    z-index: 2;
    position: absolute;
    width: 100%;
    right: 0;
    height: 0;
    top: 50%;

    button {
      background-color: rgba(255, 255, 255, 0.5);
      border: none;
      padding: 0;

      &:focus {
        outline: none;
      }

      &:hover {
        cursor: pointer;
      }
    }
  }

  .tns-controls button{ 
    &:first-child {
      float: left;
    }
    &:last-child {
      float: right;
    }
  }

  .tns-nav {
    z-index: 2;
    position: absolute;
    padding: 10px 0;
    height: 0;
    width: 100%;
    top: 0;
    text-align: center;

    button {
      background: $light-grey;
      border: none;
      width: 14px;
      margin: 1px;
      height: 18px;
      -webkit-transform: skew(-20deg);
      -moz-transform: skew(-20deg);
      -o-transform: skew(-20deg);
      border-radius: 2px;
      &:focus {
        outline: none;
      }
      &:hover {
        cursor: pointer;
      }
    }
    .tns-nav-active {
      background-color: $orange;
    }
  }

  [class^="tns-slides"] {
    padding: 0 !important; // remove padding: clientWidth = width + padding (0) = width
    [hidden] { display: none !important; }
    [aria-controls], [data-action] { cursor: pointer; }
    &.ms-touch {
      overflow-x: scroll;
      overflow-y: hidden;
    }
  }
  .tns-slider {
    -webkit-transition: all 0s;
    -moz-transition: all 0s;
    transition: all 0s;
    > .tns-item {
      -webkit-box-sizing: border-box;
      -moz-box-sizing: border-box;
      box-sizing: border-box;
    }
  }

  .tns-horizontal.tns-subpixel {
    white-space: nowrap;
    > .tns-item {
      display: inline-block;
      vertical-align: top;
      white-space: normal;
    }
  }
  .tns-horizontal.tns-no-subpixel {
    &:after {
      content: '';
      display: table;
      clear: both;
    }
    > .tns-item {
      float: left;
      margin-right: -100%;
    }
  }
  .tns-no-calc {
    position: relative;
    left: 0;
  }

  .tns-gallery {
    position: relative;
    left: 0;
    min-height: 1px; // make sure slider container is visible
    > .tns-item {
      position: absolute;
      left: -100%;
      -webkit-transition: transform 0s, opacity 0s;
      -moz-transition: transform 0s, opacity 0s;
      transition: transform 0s, opacity 0s;
    }
    > .tns-moving {
      -webkit-transition: all 0.25s;
      -moz-transition: all 0.25s;
      transition: all 0.25s;
    }
  }
  .tns-lazy-img {
    -webkit-transition: opacity 0.6s;
    -moz-transition: opacity 0.6s;
    transition: opacity 0.6s;
    opacity: 0.6;
    &.loaded { opacity: 1; }
  }
  .tns-ah {
    -webkit-transition: height 0s;
    -moz-transition: height 0s;
    transition: height 0s;
  }
  .tns-ovh { overflow: hidden; }
  .tns-hdx { overflow-x: hidden; }
  .tns-hdy { overflow-y: hidden; }
  .tns-visually-hidden { position: absolute; left: -10000em; }
  .tns-transparent { opacity: 0; visibility: hidden; }

  .tns-fadeIn {
    opacity: 1;
    filter: alpha(opacity=100);
    z-index: 0;
  }
  .tns-normal, .tns-fadeOut {
    opacity: 0;
    filter: alpha(opacity=0);
    z-index: -1;
  }
</style>




// WEBPACK FOOTER //
// src/components/slider/Slider.vue